/**
 * GuideButton — the book icon in the top bar (Sep 2026). On any page it opens
 * a drawer with the Guide doc for the app you are in (longest route match) and
 * an Ask box scoped to it; "Open in the Guide" goes to /guide?d=<id>. A dot
 * shows when there are what's-new entries for your apps you haven't read.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, X } from 'lucide-react';
import { guideDocForPath } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { GuideAsk, GuideDocBody } from './GuideParts';
import { useVisibleGuideDocs, useVisibleWhatsNew, useWhatsNewSeen } from './guide';

export function GuideButton() {
  const docs = useVisibleGuideDocs();
  const entries = useVisibleWhatsNew(docs);
  const seen = useWhatsNewSeen();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState<string | null>(null);
  const pageDoc = guideDocForPath(docs, pathname);
  const doc = (override ? docs.find((d) => d.id === override) : null) ?? pageDoc;
  const unseen = entries.some((e) => e.date > seen);

  useEffect(() => { setOpen(false); setOverride(null); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (pathname.startsWith('/guide')) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => (doc ? setOpen(true) : navigate('/guide'))}
        title={doc ? `Guide: ${doc.title}` : 'Guide'}
        aria-label={doc ? `Open the guide for ${doc.title}` : 'Open the Guide'}
        className="relative flex items-center text-fg-muted hover:text-fg transition-colors duration-fast"
      >
        <Icon as={BookOpen} size={20} />
        {unseen && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-brand-solid" aria-label="New in the Guide" />}
      </button>
      {open && doc && createPortal(
        <div className="fixed inset-0 z-modal flex justify-end bg-app/60 backdrop-blur-sm" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <aside
            role="dialog"
            aria-modal="true"
            aria-label={`Guide: ${doc.title}`}
            className="h-full w-full max-w-xl overflow-y-auto bg-app border-l border-line shadow-lg"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-app/95 backdrop-blur px-5 py-3">
              <div className="min-w-0">
                <p className="text-2xs font-semibold uppercase text-faint">Guide</p>
                <h2 className="text-base font-semibold text-body truncate">{doc.title}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/guide?d=${doc.id}`} className="text-sm text-brand underline underline-offset-2">Open in the Guide</Link>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2">
                  <Icon as={X} />
                </button>
              </div>
            </div>
            <div className="px-5 py-5 space-y-6">
              <GuideAsk docId={doc.id} docs={docs} onOpenDoc={setOverride} placeholder={`Ask anything about ${doc.title}`} />
              <GuideDocBody doc={doc} entries={entries} />
            </div>
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}
